package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.logic.NavigatorEditEmployee
import com.example.mystybag.appCore.model.pages.EditEmployeeUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.isValidEmail
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantStaffRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateMerchantStaffRequestModel
import kotlinx.coroutines.launch

class EditEmployeeViewModel(
    context: AppContext,
    config: NavigatorEditEmployee
) : ChildViewModel<EditEmployeeUIState>(context, EditEmployeeUIState(staffId = config.staffId)) {

    override fun onCreate() {
        navControls.isVisible = true
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.edit_employee)
        loadCountries()
        loadLocations()
        loadStaffAndPreFill()
    }

    fun onDeleteClick() {
        // TODO: Show delete confirmation and call delete employee API when available
    }

    private fun loadStaffAndPreFill() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        scope.launch {
            updateState { it.copy(isInitialLoad = true) }
            commandBlock(showProgress = false) {
                CommonCommands.getMerchantStaff(
                    GetMerchantStaffRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantStaffResponseModel
                                val staff = responseModel?.staffList?.find { it.id == uiState.value.staffId }
                                if (staff != null) {
                                    var phoneNumber = staff.phoneNumber?.filter { it.isDigit() } ?: ""
                                    if (phoneNumber.startsWith("0")) phoneNumber = phoneNumber.drop(1)
                                    if (phoneNumber.startsWith("359")) phoneNumber = phoneNumber.drop(3)
                                    val locationIds = staff.merchantLocationIds?.toSet()
                                        ?: emptySet<Long>()
                                    updateState {
                                        it.copy(
                                            email = staff.email ?: "",
                                            firstName = staff.firstName ?: "",
                                            lastName = staff.lastName ?: "",
                                            phoneNumber = phoneNumber,
                                            dialCode = staff.dialCode ?: "",
                                            role = staff.role?.takeIf { it.isNotBlank() } ?: "STAFF",
                                            selectedLocationIds = locationIds,
                                            canCreateBags = staff.canCreateBags,
                                            canViewRevenue = staff.canViewRevenue,
                                            isInitialLoad = false
                                        )
                                    }
                                } else {
                                    updateState { it.copy(isInitialLoad = false) }
                                }
                            }
                        },
                        baseError = {
                            launchOnMain {
                                updateState { it.copy(isInitialLoad = false) }
                            }
                        }
                    )
            }
        }
    }

    private fun loadLocations() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.getMerchantLocations(
                    GetMerchantLocationsRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
                                updateState {
                                    it.copy(locations = responseModel?.locations ?: emptyList())
                                }
                            }
                        }
                    )
            }
        }
    }

    fun toggleLocation(locationId: Long) {
        updateState { state ->
            val newSet = if (state.selectedLocationIds.contains(locationId)) {
                state.selectedLocationIds - locationId
            } else {
                state.selectedLocationIds + locationId
            }
            state.copy(
                selectedLocationIds = newSet,
                locationsIsError = false,
                locationsErrorMsg = ""
            )
        }
    }

    private fun loadCountries() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCountries()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val countries = response.countriesList?.map { country ->
                                    CountryItem(
                                        id = country.id,
                                        name = country.name,
                                        dialCode = country.dialCode,
                                        isoAlpha2 = country.isoAlpha2
                                    )
                                } ?: emptyList()
                                updateState { it.copy(countries = countries) }
                            }
                        }
                    )
            }
        }
    }

    fun showDialCodePicker() {
        updateState { it.copy(showDialCodePicker = true) }
    }

    fun hideDialCodePicker() {
        updateState { it.copy(showDialCodePicker = false) }
    }

    fun selectDialCodeCountry(dialCode: String) {
        updateState {
            it.copy(
                dialCode = dialCode,
                showDialCodePicker = false,
                dialCodeIsError = false,
                dialCodeErrorMsg = ""
            )
        }
    }

    fun onEmailChange(value: String) {
        updateState { it.copy(email = value, emailIsError = false, emailErrorMsg = "") }
    }

    fun onFirstNameChange(value: String) {
        updateState { it.copy(firstName = value, firstNameIsError = false, firstNameErrorMsg = "") }
    }

    fun onLastNameChange(value: String) {
        updateState { it.copy(lastName = value, lastNameIsError = false, lastNameErrorMsg = "") }
    }

    fun onPhoneNumberChange(value: String) {
        var cleaned = value
        while (cleaned.startsWith("0")) cleaned = cleaned.drop(1)
        if (cleaned.startsWith("+359")) cleaned = cleaned.drop(4)
        else if (cleaned.startsWith("359")) cleaned = cleaned.drop(3)
        cleaned = cleaned.filter { it.isDigit() }
        updateState { it.copy(phoneNumber = cleaned, phoneNumberIsError = false, phoneNumberErrorMsg = "") }
    }

    fun onRoleChange(role: String) {
        updateState { it.copy(role = role) }
    }

    fun onCanCreateBagsChange(value: Boolean) {
        updateState { it.copy(canCreateBags = value) }
    }

    fun onCanViewRevenueChange(value: Boolean) {
        updateState { it.copy(canViewRevenue = value) }
    }

    fun onSubmit() {
        if (!validate()) return
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = true) {
                CommonCommands.updateMerchantStaff(
                    UpdateMerchantStaffRequestModel(
                        staffId = uiState.value.staffId,
                        email = uiState.value.email,
                        firstName = uiState.value.firstName,
                        lastName = uiState.value.lastName,
                        phoneNumber = uiState.value.phoneNumber,
                        dialCode = uiState.value.dialCode,
                        role = uiState.value.role,
                        merchantLocationIds = uiState.value.selectedLocationIds.toList(),
                        canCreateBags = uiState.value.canCreateBags,
                        canViewRevenue = uiState.value.canViewRevenue
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(sharedString(SR.strings.employee_updated_successfully))
                                goBack()
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }

    private fun validate(): Boolean {
        var valid = true
        val s = uiState.value

        if (s.email.isEmpty() || !s.email.isValidEmail) {
            updateState {
                it.copy(
                    emailIsError = true,
                    emailErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.firstName.isBlank()) {
            updateState {
                it.copy(
                    firstNameIsError = true,
                    firstNameErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.lastName.isBlank()) {
            updateState {
                it.copy(
                    lastNameIsError = true,
                    lastNameErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.dialCode.isBlank()) {
            updateState {
                it.copy(
                    dialCodeIsError = true,
                    dialCodeErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.phoneNumber.isBlank()) {
            updateState {
                it.copy(
                    phoneNumberIsError = true,
                    phoneNumberErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.selectedLocationIds.isEmpty()) {
            updateState {
                it.copy(
                    locationsIsError = true,
                    locationsErrorMsg = sharedString(SR.strings.select_at_least_one_location)
                )
            }
            valid = false
        }

        return valid
    }
}
